import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {DistinctDomainWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {domainColor, distinctDomainToFieldLabel} from 'antibodies/AntibodyUtils';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {
  Filter,
  FilterKey,
  GLOBAL_SEARCH_URL,
  keyToEmptyFilter,
  Operator,
  SortKey,
} from 'search/SearchConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import BenchChip from 'ui-library/chips/BenchChip';
import ChipSearchInput, {searchPost} from 'ui-library/inputs/ChipSearchInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export interface AntibodyDomainSearchInputProps {
  disabled: boolean;
  distinctDomain: DistinctDomainWithChainIndex;
  selectedProtein: string | null;
  setSelectedProtein: (newSelectedProtein: string | null) => void;
  linkedSchemaId: string | null;
  showSwatch?: boolean;
  registryId?: string;
}

/**
 * A wrapper around ChipSearchInput that can be used to associate an AA Sequence entity with an antibody domain
 * @param disabled Whether the ChipSearchInput should be disabled
 * @param distinctDomain The domain type / specificity to set an AA Sequence for
 * @param selectedProtein The API ID of the selected AA Sequence, if any
 * @param setSelectedProtein A function to update the selected protein
 * @param linkedSchemaId The API ID of the schema that the domain must be. null if any AA sequence is allowed
 * @param showSwatch Whether to show the swatch stripe. Defaults to true.
 */
export function AntibodyDomainSearchInput({
  disabled,
  distinctDomain,
  selectedProtein,
  setSelectedProtein,
  linkedSchemaId,
  registryId,
  showSwatch = true,
}: AntibodyDomainSearchInputProps): JSX.Element {
  const [query, setQuery] = useState('');

  return (
    <div className={css(styles.wrapper)} aria-label={distinctDomainToFieldLabel(distinctDomain)}>
      {showSwatch && (
        <div className={css(styles.swatch)} style={{backgroundColor: domainColor({distinctDomain}).fill}} />
      )}
      <FormGroup className={css(styles.searchWrapper)}>
        <FieldLabel label={distinctDomainToFieldLabel(distinctDomain)} isRequired={true} />
        <ChipSearchInput
          autoFocus={false}
          chipClass={BenchChip}
          disabled={disabled}
          doSearch={searchPost}
          fetchParams={{
            offset: 0,
            limit: 10,
            sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
            filters: [
              {
                ...keyToEmptyFilter(FilterKey.TYPES),
                value: [ModelTypeConstants.PROTEIN],
              },
              // TODO: Do we instead want special visual treatment for archived schemas?
              {
                ...keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
                value: [NOT_ARCHIVED_PURPOSE_STRING],
              },
              // TODO: Do we instead want special visual treatment for empty proteins?
              {
                ...keyToEmptyFilter(FilterKey.PROTEIN_AMINO_ACIDS),
                operator: Operator.IS_NOT_EMPTY,
              },
              ...(registryId != null
                ? [{key: FilterKey.REGISTRY, operator: Operator.IS, value: registryId} satisfies Filter]
                : []),
              ...(linkedSchemaId != null
                ? [{...keyToEmptyFilter(FilterKey.SCHEMA), value: [linkedSchemaId]}]
                : []),
            ],
          }}
          getChipProps={(id) => ({
            id,
          })}
          getOptionDatasFromResponse={(response: GlobalSearchResponse) => {
            const {ids, fileSummariesById} = response;
            return ids.map((id) => {
              const summary = fileSummariesById[id];
              return {
                name: summary.name,
                icon: summary.tagSchema?.iconId ?? 'protein',
                value: id,
              };
            });
          }}
          isMulti={false}
          query={query}
          onQueryChange={(e) => {
            setQuery(e.target.value);
          }}
          onRemove={() => {
            setSelectedProtein(null);
          }}
          onSelect={(value: string) => {
            setSelectedProtein(value);
            setQuery('');
          }}
          placeholder="Search for protein"
          searchOnEmptyQuery={false}
          searchURL={GLOBAL_SEARCH_URL}
          value={compact([selectedProtein])}
        />
      </FormGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {display: 'flex'},
  swatch: {
    width: '4px',
    minWidth: '4px',
    marginRight: '4px',
    flex: '0 0 4px',
    // These values are somewhat arbitrary. They are meant to make the swatch align with the top of the
    // domain label text and the bottom of the domain search box.
    marginTop: '3px',
    marginBottom: '15px',
  },
  searchWrapper: {width: '100%'},
});
